import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { extractErrorMessage } from '../api/errorUtils';
import axiosInstance from '../api/axiosInstance';
import { showNotification } from './notificationSlice';

// Full endpoint paths from your API
const ENDPOINTS = {
  login: 'techsavanna_pos.api.auth_api.login_user',
  me: 'techsavanna_pos.api.auth_api.get_current_user',
  userPermissions: 'techsavanna_pos.api.auth_api.get_user_permissions',
  refreshToken: 'techsavanna_pos.api.auth_api.refresh_token',
  updateProfile: 'techsavanna_pos.api.auth_api.update_user_profile',
  changePassword: 'techsavanna_pos.api.auth_api.change_password',
  getPOSIndustries: 'techsavanna_pos.api.industry_api.get_pos_industries',
  getUserIndustry: 'techsavanna_pos.api.industry_api.get_user_industry',
  sendOTPEmail: 'techsavanna_pos.api.verification_api.send_email_verification',
  verifyOTPEmail: 'techsavanna_pos.api.verification_api.verify_email_code',
  logout: 'techsavanna_pos.api.auth_api.logout_user',
  resetPassword: 'techsavanna_pos.api.auth_api.reset_password',
};

// Helper function to extract data from nested message response
const extractResponseData = (response) => {

  // Debug: Log raw response structure
  if (process.env.NODE_ENV === 'development') {
    console.log('extractResponseData - Raw response.data:', response.data);
  }

  if (response.data?.message && typeof response.data.message === 'object') {
    // Check if message is the actual data object (has user, company, etc.) or if it's nested further
    const messageData = response.data.message;

    // Debug: Log message data
    if (process.env.NODE_ENV === 'development') {
      console.log('extractResponseData - messageData:', messageData);
    }

    // If message has a 'message' property that's an object, it might be double-nested
    if (messageData.message && typeof messageData.message === 'object') {
      if (process.env.NODE_ENV === 'development') {
        console.log('extractResponseData - Double-nested, returning messageData.message');
      }
      return messageData.message;
    }

    if (process.env.NODE_ENV === 'development') {
      console.log('extractResponseData - Returning messageData');
    }
    return messageData;
  }

  const result = response.data?.message || response.data;
  if (process.env.NODE_ENV === 'development') {
    console.log('extractResponseData - Fallback, returning:', result);
  }
  return result;
};

// Helper function to extract success message from response
const extractSuccessMessage = (response) => {
  // Check if message is a string at root level
  if (response.data?.message && typeof response.data.message === 'string') {
    return response.data.message;
  }
  // Check if message is nested in message object (new structure)
  if (response.data?.message && typeof response.data.message === 'object') {
    // The message object contains a 'message' property with the success message
    if (response.data.message.message && typeof response.data.message.message === 'string') {
      return response.data.message.message;
    }
  }
  return null;
};

// Helper function to extract error message
// Helper function to determine notification severity based on HTTP status code
const getErrorSeverity = (error) => {
  // 409 Conflict should be shown as warning instead of error
  if (error.response?.status === 409) {
    return 'warning';
  }
  return 'error';
};


// Helper function to check if token is valid
// NOTE: Tokens are OAuth Bearer tokens (random strings), NOT JWTs.
// Validation is done against the stored expires_at value from localStorage.
const isTokenValid = (token) => {
  if (!token) return false;
  const expiresAt = localStorage.getItem('expires_at');
  if (expiresAt) {
    try {
      return Date.parse(expiresAt) > Date.now();
    } catch {
      // fall through to just checking token existence
    }
  }
  return true;
};

/**
 * Initialize state from localStorage
 * 
 * isAuthenticated is determined by:
 * 1. Presence of a valid access_token in localStorage
 * 2. Token validity (not expired) checked via jwtDecode
 * 
 * The token is considered valid if:
 * - It exists in localStorage
 * - It can be decoded as a JWT
 * - The expiration time (exp) is greater than the current time
 * 
 * Note: isAuthenticated does NOT depend on the user object being present,
 * only on the token validity. This allows the app to remain authenticated
 * even if the user data hasn't been fetched yet.
 */
const getInitialState = () => {
  const token = localStorage.getItem('access_token');
  const refreshToken = localStorage.getItem('refresh_token');
  const userStr = localStorage.getItem('user');
  const apiKey = localStorage.getItem('api_key');
  const apiSecret = localStorage.getItem('api_secret');

  let user = null;
  if (userStr) {
    try {
      user = JSON.parse(userStr);
    } catch (error) {
      console.error('Error parsing user from localStorage:', error);
    }
  }

  // isAuthenticated is determined solely by token presence and validity
  // It does NOT depend on user object being present
  const isAuthenticated = token && isTokenValid(token);

  return {
    user,
    token,
    refreshToken,
    apiKey,
    apiSecret,
    isAuthenticated,
    isLoading: false,
    error: null,
    industries: [],
    isLoadingIndustries: false,
    // Temporary storage for tokens before OTP verification
    tempAuth: null,
    // OTP state
    otp: {
      isLoading: false,
      isVerifying: false,
      error: null,
      verified: false,
      email: null,
      expires_in_minutes: null,
      verified_at: null,
    },
  };
};

// Async thunks
export const loginUser = createAsyncThunk(
  'auth/login',
  async ({ email, password }, { rejectWithValue, dispatch }) => {
    try {
      const response = await axiosInstance.post(ENDPOINTS.login, {
        email,
        password,
      });
      const data = extractResponseData(response);
      const successMessage = extractSuccessMessage(response);

      // New response structure: access_token, refresh_token are directly in message object
      const { access_token, refresh_token, user, api_key, api_secret, token_type, expires_in, expires_at } = data;
      
      // If in development mode, bypass OTP by storing tokens directly
      if (process.env.NODE_ENV === 'development' || true /* TEMPORARILY DISABLED OTP VERIFICATION */) {
        if (access_token) localStorage.setItem('access_token', access_token);
        if (refresh_token) localStorage.setItem('refresh_token', refresh_token);
        if (user) localStorage.setItem('user', JSON.stringify(user));
        if (api_key) localStorage.setItem('api_key', api_key);
        if (api_secret) localStorage.setItem('api_secret', api_secret);
        if (expires_at) localStorage.setItem('expires_at', expires_at);
      }

      // Show success notification
      if (successMessage) {
        dispatch(showNotification({
          message: successMessage,
          severity: 'success',
          title: 'Login Successful',
        }));
      }

      return {
        user,
        token: access_token,
        refreshToken: refresh_token,
        tokenType: token_type,
        expiresIn: expires_in,
        expiresAt: expires_at,
        apiKey: api_key,
        apiSecret: api_secret,
      };
    } catch (error) {
      const errorMessage = extractErrorMessage(error);

      // Show error notification
      dispatch(showNotification({
        message: errorMessage,
        severity: getErrorSeverity(error),
        title: 'Login Failed',
      }));

      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Fetches the current user's full profile from the API.
 * 
 * This function retrieves the complete user profile including:
 * - User basic info (name, email, etc.)
 * - Company information
 * - POS profile
 * - Roles and permissions
 * - Default warehouse
 * - POS industry
 * 
 * The response structure from the API is:
 * {
 *   "message": {
 *     "user": {...},
 *     "company": {...},
 *     "pos_profile": {...},
 *     "pos_industry": {...},
 *     "roles": [...],
 *     "permissions": {...},
 *     "default_warehouse": "..."
 *   }
 * }
 * 
 * This function merges all this data into a single userProfile object
 * and stores it in both Redux state and localStorage.
 */
/**
 * Fetch the current user's aggregated permissions (module + doctype) across all their roles.
 * Call this whenever the user's roles may have changed (e.g. after an admin role reassignment).
 * The result is merged back into the stored user profile in Redux and localStorage.
 */
export const fetchUserPermissions = createAsyncThunk(
  'auth/fetchUserPermissions',
  async (_, { rejectWithValue, getState, dispatch }) => {
    try {
      const response = await axiosInstance.get(ENDPOINTS.userPermissions);
      const data = extractResponseData(response);
      return {
        roles: data.roles || [],
        module_permissions: data.module_permissions || {},
        doctype_permissions: data.doctype_permissions || {},
      };
    } catch (error) {
      return rejectWithValue(extractErrorMessage(error));
    }
  }
);

export const fetchCurrentUser = createAsyncThunk(
  'auth/fetchCurrentUser',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      // Fetch profile and permissions in parallel to minimise login latency
      const [response, permsResponse] = await Promise.all([
        axiosInstance.get(ENDPOINTS.me),
        axiosInstance.get(ENDPOINTS.userPermissions).catch(() => null),
      ]);
      const data = extractResponseData(response);
      const permsData = permsResponse ? extractResponseData(permsResponse) : null;

      // New response structure: { user: {...}, company: {...}, pos_profile: {...}, default_warehouse: "...", pos_industry: {...}, roles: [...], permissions: {...} }
      const userData = data.user || data;
      const companyData = data.company || null;
      const posProfileData = data.pos_profile || null;
      const defaultWarehouseName = data.default_warehouse || null;
      const posIndustryData = data.pos_industry || data.primary || null;
      const posIndustriesData = data.pos_industries || data.industries || [];
      // Normalize roles to an array of plain role-name strings. The backend may
      // return roles as strings or as objects ({ role } / { name } / { role_name });
      // role-based access lookups (ROLE_ACCESS_CONFIG) key strictly on the string
      // name, so an un-normalized object shape silently denies every page.
      const rawRoles = data.roles || userData.roles || [];
      const userRoles = (Array.isArray(rawRoles) ? rawRoles : [])
        .map((r) => (typeof r === 'string' ? r : (r?.role || r?.name || r?.role_name || '')))
        .filter(Boolean);
      const userPermissions = data.permissions || null;
      const businessCapabilities = data.business_capabilities || null;
      // Prefer the dedicated permissions endpoint (union across all roles); fall back to what
      // get_current_user returns for backwards-compatibility.
      const userModulePermissions =
        (permsData?.module_permissions && Object.keys(permsData.module_permissions).length > 0)
          ? permsData.module_permissions
          : (data.module_permissions || {});
      const userDocTypePermissions =
        (permsData?.doctype_permissions && Object.keys(permsData.doctype_permissions).length > 0)
          ? permsData.doctype_permissions
          : (data.doctype_permissions || {});

      // Merge user and company data into a comprehensive userProfile
      const userProfile = {
        ...userData,
        // Store roles array for role-based access control
        roles: Array.isArray(userRoles) ? userRoles : [],
        // Store permissions object
        permissions: userPermissions,
        // Store capability-based permissions for granular UI access
        business_capabilities: businessCapabilities,
        // Add company information to user object for easy access
        company: companyData?.name || companyData?.company_name || null,
        company_name: companyData?.company_name || companyData?.name || null,
        company_abbr: companyData?.abbr || null,
        company_currency: companyData?.default_currency || null,
        company_country: companyData?.country || null,
        company_tax_id: companyData?.tax_id || null,
        company_phone: companyData?.phone_no || null,
        company_email: companyData?.email || null,
        company_website: companyData?.website || null,
        company_address: companyData?.address || null,
        // Store full company object for reference
        company_data: companyData,
        // Store POS profile information
        pos_profile: posProfileData?.name || null,
        pos_profile_data: posProfileData,
        // Store default warehouse name
        default_warehouse: defaultWarehouseName,
        // Store POS industry information
        pos_industry: posIndustryData,
        pos_industry_code: (typeof posIndustryData === 'string' ? posIndustryData : (posIndustryData?.name || posIndustryData?.industry_code || null)),
        pos_industry_name: posIndustryData?.industry_name || (typeof posIndustryData === 'string' ? posIndustryData : null),
        // Store multiple industries if present
        pos_industries: Array.isArray(posIndustriesData) ? posIndustriesData : [],
        // Store aggregated module permissions from all user roles
        module_permissions: userModulePermissions,
        // Store doctype-based permissions for route protection
        doctype_permissions: userDocTypePermissions,
      };

      // Store complete user profile in localStorage
      localStorage.setItem('user', JSON.stringify(userProfile));

      // Also store company separately if needed
      if (companyData) {
        localStorage.setItem('company', JSON.stringify(companyData));
      }

      // Store POS profile separately if needed
      if (posProfileData) {
        localStorage.setItem('pos_profile', JSON.stringify(posProfileData));
      }

      return userProfile;
    } catch (error) {
      const errorMessage = extractErrorMessage(error);
      const statusCode = error.response?.status;

      dispatch(showNotification({
        message: errorMessage,
        severity: getErrorSeverity(error),
        title: 'Failed to fetch user profile',
      }));

      return rejectWithValue({
        message: errorMessage,
        status: statusCode
      });
    }
  }
);

export const refreshToken = createAsyncThunk(
  'auth/refreshToken',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const response = await axiosInstance.post(ENDPOINTS.refreshToken);
      const data = extractResponseData(response);
      const successMessage = extractSuccessMessage(response);

      // New response structure: access_token, refresh_token are directly in message object
      const { access_token, refresh_token, token_type, expires_in, expires_at } = data;

      // Store in localStorage
      if (access_token) localStorage.setItem('access_token', access_token);
      if (refresh_token) localStorage.setItem('refresh_token', refresh_token);
      if (expires_at) localStorage.setItem('expires_at', expires_at);

      // Show success notification
      if (successMessage) {
        dispatch(showNotification({
          message: successMessage,
          severity: 'success',
          title: 'Token Refreshed',
        }));
      }

      return {
        token: access_token,
        refreshToken: refresh_token,
        tokenType: token_type,
        expiresIn: expires_in,
        expiresAt: expires_at,
      };
    } catch (error) {
      const errorMessage = extractErrorMessage(error);

      // Show error notification
      dispatch(showNotification({
        message: errorMessage,
        severity: getErrorSeverity(error),
        title: 'Token Refresh Failed',
      }));

      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Logout User
 * Sends a POST request to the server to invalidate the session,
 * then clears local auth state and handles redirection.
 */
export const logoutUser = createAsyncThunk(
  'auth/logoutUser',
  async (_, { dispatch }) => {
    try {
      const response = await axiosInstance.post(ENDPOINTS.logout);
      const data = extractResponseData(response);
      const successMessage = data?.message || 'Logged out successfully';

      dispatch(showNotification({
        message: successMessage,
        severity: 'success',
        title: 'Logout Successful',
      }));
    } catch (error) {
      // Still logout locally even if server call fails
      console.error('Server-side logout failed:', error);
    } finally {
      // Always clear local state
      dispatch(authSlice.actions.logout());
    }
  }
);

export const updateUserProfile = createAsyncThunk(
  'auth/updateProfile',
  async (profileData, { rejectWithValue, dispatch }) => {
    try {
      const response = await axiosInstance.put(ENDPOINTS.updateProfile, profileData);
      const data = extractResponseData(response);
      const successMessage = extractSuccessMessage(response);
      const user = data.user;

      localStorage.setItem('user', JSON.stringify(user));

      // Show success notification
      if (successMessage) {
        dispatch(showNotification({
          message: successMessage,
          severity: 'success',
          title: 'Profile Updated',
        }));
      }

      return user;
    } catch (error) {
      const errorMessage = extractErrorMessage(error);

      // Show error notification
      dispatch(showNotification({
        message: errorMessage,
        severity: getErrorSeverity(error),
        title: 'Profile Update Failed',
      }));

      return rejectWithValue(errorMessage);
    }
  }
);

export const changePassword = createAsyncThunk(
  'auth/changePassword',
  async ({ old_password, new_password }, { rejectWithValue, dispatch }) => {
    try {
      const response = await axiosInstance.post(ENDPOINTS.changePassword, {
        old_password,
        new_password,
      });
      const data = extractResponseData(response);
      const successMessage = extractSuccessMessage(response) || data.message || 'Password changed successfully';

      // Show success notification
      dispatch(showNotification({
        message: successMessage,
        severity: 'success',
        title: 'Password Changed',
      }));

      return successMessage;
    } catch (error) {
      const errorMessage = extractErrorMessage(error);

      // Show error notification
      dispatch(showNotification({
        message: errorMessage,
        severity: getErrorSeverity(error),
        title: 'Password Change Failed',
      }));

      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Reset Password
 * Resets a user's password using their email and a new password.
 * Typically called after successful OTP verification.
 * 
 * @param {Object} params - Parameters object
 * @param {string} params.email - User's email address
 * @param {string} params.new_password - New password to set
 */
export const resetPassword = createAsyncThunk(
  'auth/resetPassword',
  async ({ email, new_password }, { rejectWithValue, dispatch }) => {
    try {
      const response = await axiosInstance.post(ENDPOINTS.resetPassword, {
        email,
        new_password,
      });
      const data = extractResponseData(response);
      const successMessage = extractSuccessMessage(response) || data.message || 'Password reset successfully';

      // Show success notification
      dispatch(showNotification({
        message: successMessage,
        severity: 'success',
        title: 'Password Reset',
      }));

      return successMessage;
    } catch (error) {
      const errorMessage = extractErrorMessage(error);

      // Show error notification
      dispatch(showNotification({
        message: errorMessage,
        severity: getErrorSeverity(error),
        title: 'Password Reset Failed',
      }));

      return rejectWithValue(errorMessage);
    }
  }
);

export const getPOSIndustries = createAsyncThunk(
  'auth/getPOSIndustries',
  async (params = { is_active: true }, { rejectWithValue, dispatch }) => {
    try {
      const response = await axiosInstance.get(ENDPOINTS.getPOSIndustries, { params });
      const data = extractResponseData(response);
      return data.industries || [];
    } catch (error) {
      // Check for CORS errors specifically
      const errorMessage = error.message || '';
      const isCorsError = errorMessage.includes('CORS') ||
        errorMessage.includes('Access-Control-Allow-Origin') ||
        (!error.response && error.request);

      if (isCorsError) {
        const isDuplicateHeaderError = errorMessage.includes('multiple values') &&
          errorMessage.includes('Access-Control-Allow-Origin');

        const corsErrorMessage = isDuplicateHeaderError
          ? 'Server configuration error: Duplicate CORS headers detected. Please contact the server administrator to fix the CORS configuration.'
          : 'CORS error: Request blocked by browser. Please check server CORS configuration.';

        // Show user-friendly notification
        dispatch(showNotification({
          message: corsErrorMessage,
          severity: getErrorSeverity(error),
          title: 'Connection Error',
        }));

        return rejectWithValue(corsErrorMessage);
      }

      const errorMessageText = extractErrorMessage(error);
      return rejectWithValue(errorMessageText);
    }
  }
);

/**
 * Send OTP Email
 * Sends a 6-digit OTP code to the specified email address.
 * 
 * @param {Object} params - Parameters object
 * @param {string} params.email - Email address to send OTP to
 */
export const sendOTPEmail = createAsyncThunk(
  'auth/sendOTPEmail',
  async ({ email, purpose = 'verification' }, { rejectWithValue, dispatch }) => {
    try {
      // For flows that act on an existing account (password reset, login 2FA),
      // ask the backend to refuse if the address is not registered — otherwise
      // we'd leak OTPs to random inboxes and burn SMTP quota.
      const requireExistingUser =
        purpose === 'password_reset' || purpose === 'login_verification';

      const response = await axiosInstance.post(ENDPOINTS.sendOTPEmail, {
        email,
        require_existing_user: requireExistingUser,
        purpose,
      });

      // Use extractResponseData to handle the nested message structure
      const data = extractResponseData(response);

      if (data.success === false) {
        const errorMessage = data.error || data.message || 'Failed to send verification email';
        dispatch(showNotification({
          message: errorMessage,
          severity: 'error',
          title: 'Verification Send Failed',
        }));
        return rejectWithValue(errorMessage);
      }

      const successMessage = data.message || 'Verification code has been sent to your email address';
      dispatch(showNotification({
        message: successMessage,
        severity: 'success',
        title: 'Verification Code Sent',
      }));

      return {
        success: true,
        message: successMessage,
        expires_in_minutes: data.expires_in_minutes || 15,
        email: data.email || email,
      };
    } catch (error) {
      const errorMessage = extractErrorMessage(error);

      dispatch(showNotification({
        message: errorMessage,
        severity: getErrorSeverity(error),
        title: 'Verification Send Failed',
      }));

      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Verify OTP Email
 * Verifies the OTP code sent to an email address.
 * 
 * @param {Object} params - Parameters object
 * @param {string} params.email - Email address that received the OTP
 * @param {string} params.code - 6-digit OTP code to verify
 */
export const verifyOTPEmail = createAsyncThunk(
  'auth/verifyOTPEmail',
  async ({ email, code, purpose = 'general' }, { rejectWithValue, dispatch }) => {
    try {
      const response = await axiosInstance.post(ENDPOINTS.verifyOTPEmail, {
        email,
        code,
        purpose,
      });

      // Use extractResponseData to handle the nested message structure
      const data = extractResponseData(response);

      if (!data.success || !data.verified) {
        const errorMessage = data.error || data.message || 'Verification code verification failed';
        dispatch(showNotification({
          message: errorMessage,
          severity: 'error',
          title: 'Verification Failed',
        }));
        return rejectWithValue(errorMessage);
      }

      const successMessage = data.message || 'Email verified successfully';
      dispatch(showNotification({
        message: successMessage,
        severity: 'success',
        title: 'Email Verified',
      }));

      return {
        success: data.success,
        verified: data.verified,
        message: successMessage,
        email: data.email || email,
        verified_at: data.verified_at,
      };
    } catch (error) {
      const errorMessage = extractErrorMessage(error);

      dispatch(showNotification({
        message: errorMessage,
        severity: getErrorSeverity(error),
        title: 'Verification Failed',
      }));

      return rejectWithValue(errorMessage);
    }
  }
);

// Auth slice
const authSlice = createSlice({
  name: 'auth',
  initialState: getInitialState(),
  reducers: {
    logout: (state) => {
      state.user = null;
      state.token = null;
      state.refreshToken = null;
      state.apiKey = null;
      state.apiSecret = null;
      state.isAuthenticated = false;
      state.error = null;

      // Clear localStorage
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
      localStorage.removeItem('api_key');
      localStorage.removeItem('api_secret');
      localStorage.removeItem('expires_at');
    },
    clearError: (state) => {
      state.error = null;
    },
    checkAuth: (state) => {
      const token = localStorage.getItem('access_token');
      if (token && isTokenValid(token)) {
        state.isAuthenticated = true;
        state.token = token;
        const userStr = localStorage.getItem('user');
        if (userStr) {
          try {
            state.user = JSON.parse(userStr);
          } catch (error) {
            console.error('Error parsing user from localStorage:', error);
          }
        }
      } else {
        state.isAuthenticated = false;
        state.token = null;
        state.user = null;
      }
    },
    clearOTPState: (state) => {
      state.otp = {
        isLoading: false,
        isVerifying: false,
        error: null,
        verified: false,
        email: null,
        expires_in_minutes: null,
        verified_at: null,
      };
    },
  },
  extraReducers: (builder) => {
    // Login
    builder
      .addCase(loginUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.tempAuth = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.isLoading = false;
        // Store in tempAuth instead of user/token to avoid premature redirect
        state.tempAuth = {
          user: action.payload.user,
          token: action.payload.token,
          refreshToken: action.payload.refreshToken,
          apiKey: action.payload.apiKey,
          apiSecret: action.payload.apiSecret,
        };

        // If in development mode, we bypass OTP and set authenticated directly
        if (process.env.NODE_ENV === 'development' || true /* TEMPORARILY DISABLED OTP VERIFICATION */) {
          state.user = action.payload.user;
          state.token = action.payload.token;
          state.refreshToken = action.payload.refreshToken;
          state.apiKey = action.payload.apiKey;
          state.apiSecret = action.payload.apiSecret;
          state.isAuthenticated = true; // Bypassing OTP
          state.tempAuth = null;
        } else {
          state.isAuthenticated = false; // Stay unauthenticated until OTP is verified
        }
        state.error = null;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = typeof action.payload === 'object' ? action.payload.message : action.payload;
        state.isAuthenticated = false;
        state.tempAuth = null;
      });

    // Fetch current user
    builder
      .addCase(fetchCurrentUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        // Don't change isAuthenticated during fetch - keep it as is
      })
      .addCase(fetchCurrentUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload;
        if (action.payload?.custom_has_pin) {
          localStorage.setItem('pin_login_enabled', 'true');
        } else {
          localStorage.removeItem('pin_login_enabled');
        }
        state.isAuthenticated = true; // Ensure authenticated state is set
        state.error = null;
      })
      .addCase(fetchCurrentUser.rejected, (state, action) => {
        state.isLoading = false;
        const payload = action.payload;
        state.error = typeof payload === 'object' ? payload.message : payload;

        // If unauthorized (401), clear auth state to prevent reload loops
        if (payload?.status === 401) {
          state.user = null;
          state.token = null;
          state.refreshToken = null;
          state.apiKey = null;
          state.apiSecret = null;
          state.isAuthenticated = false;

          // Also ensure localStorage is cleared (redundant with interceptor but safer)
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('user');
          localStorage.removeItem('api_key');
          localStorage.removeItem('api_secret');
          localStorage.removeItem('expires_at');
        }
      });

    // Refresh token
    builder
      .addCase(refreshToken.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(refreshToken.fulfilled, (state, action) => {
        state.isLoading = false;
        state.token = action.payload.token;
        state.refreshToken = action.payload.refreshToken;
        state.error = null;
      })
      .addCase(refreshToken.rejected, (state, action) => {
        state.isLoading = false;
        state.error = typeof action.payload === 'object' ? action.payload.message : action.payload;
        // If token refresh fails, logout user
        state.isAuthenticated = false;
        state.token = null;
        state.user = null;
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user');
        localStorage.removeItem('expires_at');
      });

    // Update profile
    builder
      .addCase(updateUserProfile.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateUserProfile.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload;
        state.error = null;
      })
      .addCase(updateUserProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    // Change password
    builder
      .addCase(changePassword.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(changePassword.fulfilled, (state) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(changePassword.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    // Get POS Industries
    builder
      .addCase(getPOSIndustries.pending, (state) => {
        state.isLoadingIndustries = true;
      })
      .addCase(getPOSIndustries.fulfilled, (state, action) => {
        state.isLoadingIndustries = false;
        state.industries = action.payload;
      })
      .addCase(getPOSIndustries.rejected, (state) => {
        state.isLoadingIndustries = false;
      });

    // Send OTP Email
    builder
      .addCase(sendOTPEmail.pending, (state) => {
        state.otp.isLoading = true;
        state.otp.error = null;
      })
      .addCase(sendOTPEmail.fulfilled, (state, action) => {
        state.otp.isLoading = false;
        state.otp.error = null;
        state.otp.email = action.payload.email;
        state.otp.expires_in_minutes = action.payload.expires_in_minutes;
        state.otp.verified = false;
      })
      .addCase(sendOTPEmail.rejected, (state, action) => {
        state.otp.isLoading = false;
        state.otp.error = action.payload;
      });

    // Verify OTP Email
    builder
      .addCase(verifyOTPEmail.pending, (state) => {
        state.otp.isVerifying = true;
        state.otp.error = null;
      })
      .addCase(verifyOTPEmail.fulfilled, (state, action) => {
        state.otp.isVerifying = false;
        state.otp.verified = true;
        state.otp.email = action.payload.email;
        state.otp.verified_at = action.payload.verified_at;
        state.otp.error = null;

        // Move tempAuth to main auth state and localStorage
        if (state.tempAuth) {
          const { user, token, refreshToken, apiKey } = state.tempAuth;
          state.user = user;
          state.token = token;
          state.refreshToken = refreshToken;
          state.apiKey = apiKey;
          state.isAuthenticated = true;

          if (token) localStorage.setItem('access_token', token);
          if (refreshToken) localStorage.setItem('refresh_token', refreshToken);
          if (user) localStorage.setItem('user', JSON.stringify(user));
          if (apiKey) localStorage.setItem('api_key', apiKey);

          state.tempAuth = null;
        }
      })
      .addCase(verifyOTPEmail.rejected, (state, action) => {
        state.otp.isVerifying = false;
        state.otp.verified = false;
        state.otp.error = action.payload;
      });

    // Login with PIN
    builder
      .addCase(loginWithPin.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginWithPin.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.refreshToken = action.payload.refreshToken;
        state.apiKey = action.payload.apiKey;
        state.apiSecret = action.payload.apiSecret;
        state.isAuthenticated = true;
        state.error = null;
      })
      .addCase(loginWithPin.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    // Set User PIN
    builder
      .addCase(setUserPin.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(setUserPin.fulfilled, (state) => {
        state.isLoading = false;
        state.error = null;
        localStorage.setItem('pin_login_enabled', 'true');
        if (state.user) {
          state.user = { ...state.user, custom_has_pin: true };
          localStorage.setItem('user', JSON.stringify(state.user));
        }
      })
      .addCase(setUserPin.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    // Clear User PIN
    builder
      .addCase(clearUserPin.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(clearUserPin.fulfilled, (state) => {
        state.isLoading = false;
        state.error = null;
        localStorage.removeItem('pin_login_enabled');
        if (state.user) {
          state.user = { ...state.user, custom_has_pin: false };
          localStorage.setItem('user', JSON.stringify(state.user));
        }
      })
      .addCase(clearUserPin.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    // Fetch user permissions (standalone re-fetch after role changes)
    builder
      .addCase(fetchUserPermissions.fulfilled, (state, action) => {
        if (state.user) {
          const { roles, module_permissions, doctype_permissions } = action.payload;
          state.user = {
            ...state.user,
            ...(roles.length > 0 && { roles }),
            module_permissions,
            doctype_permissions,
          };
          localStorage.setItem('user', JSON.stringify(state.user));
        }
      });
  },
});

export const loginWithPin = createAsyncThunk(
  'auth/loginWithPin',
  async ({ pin }, { rejectWithValue, dispatch }) => {
    try {
      const response = await axiosInstance.post(
        'techsavanna_pos.api.auth_api.login_with_pin',
        { pin }
      );
      const data = extractResponseData(response);

      const { access_token, refresh_token, user, api_key, api_secret, token_type, expires_in, expires_at } = data;

      if (access_token) localStorage.setItem('access_token', access_token);
      if (refresh_token) localStorage.setItem('refresh_token', refresh_token);
      if (user) localStorage.setItem('user', JSON.stringify(user));
      if (api_key) localStorage.setItem('api_key', api_key);
      if (api_secret) localStorage.setItem('api_secret', api_secret);
      if (expires_at) localStorage.setItem('expires_at', expires_at);

      dispatch(showNotification({
        message: data.message || 'Login successful',
        severity: 'success',
        title: 'Login Successful',
      }));

      return {
        user,
        token: access_token,
        refreshToken: refresh_token,
        tokenType: token_type,
        expiresIn: expires_in,
        expiresAt: expires_at,
        apiKey: api_key,
        apiSecret: api_secret,
      };
    } catch (error) {
      return rejectWithValue(extractErrorMessage(error));
    }
  }
);

export const setUserPin = createAsyncThunk(
  'auth/setUserPin',
  async ({ pin, user }, { rejectWithValue, dispatch }) => {
    try {
      const response = await axiosInstance.post(
        'techsavanna_pos.api.auth_api.set_user_pin',
        { pin, user }
      );
      const data = extractResponseData(response);
      dispatch(showNotification({
        message: data.message || 'PIN set successfully',
        severity: 'success',
        title: 'PIN Updated',
      }));
      return data;
    } catch (error) {
      dispatch(showNotification({
        message: extractErrorMessage(error),
        severity: 'error',
        title: 'PIN Update Failed',
      }));
      return rejectWithValue(extractErrorMessage(error));
    }
  }
);

export const clearUserPin = createAsyncThunk(
  'auth/clearUserPin',
  async ({ user }, { rejectWithValue, dispatch }) => {
    try {
      const response = await axiosInstance.post(
        'techsavanna_pos.api.auth_api.remove_user_pin',
        { user }
      );
      const data = extractResponseData(response);
      dispatch(showNotification({
        message: data.message || 'PIN removed successfully',
        severity: 'success',
        title: 'PIN Removed',
      }));
      return data;
    } catch (error) {
      dispatch(showNotification({
        message: extractErrorMessage(error),
        severity: 'error',
        title: 'PIN Removal Failed',
      }));
      return rejectWithValue(extractErrorMessage(error));
    }
  }
);

export const { logout, clearError, checkAuth, clearOTPState } = authSlice.actions;
export default authSlice.reducer;
